import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import { api } from '../services/api';

type UserRole = 'super_admin' | 'admin' | 'manager' | 'employee';

interface User {
  email: string;
  full_name: string;
  role: UserRole;
  id: string;
  is_super_admin: boolean;
  must_change_password?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  hasPermission: (requiredRole: UserRole) => boolean;
  canManageEmployee: (targetRole: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Role hierarchy for permission checks
const ROLE_HIERARCHY: Record<UserRole, number> = {
  super_admin: 4,
  admin: 3,
  manager: 2,
  employee: 1,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('refresh_token');
      }
    }
    setIsLoading(false);
  }, []);

  // Listen for auth:logout events from API client (triggered when refresh token expires)
  useEffect(() => {
    const handleAuthLogout = () => {
      setToken(null);
      setUser(null);
      message.warning('Your session has expired. Please log in again.');
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.login(email, password);
      const { access_token, refresh_token, user: userData } = response;

      setToken(access_token);
      setUser(userData);

      // Store in localStorage
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('auth_user', JSON.stringify(userData));

      message.success('Login successful!');
    } catch (error: unknown) {
      console.error('Login error:', error);
      const err = error as { response?: { data?: { detail?: string } } };
      message.error(err.response?.data?.detail || 'Login failed');
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_user');
    message.info('Logged out successfully');
  };

  // Check if user has specific role(s)
  const hasRole = (role: UserRole | UserRole[]): boolean => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  };

  // Check if user has permission (role hierarchy)
  const hasPermission = (requiredRole: UserRole): boolean => {
    if (!user) return false;
    return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[requiredRole];
  };

  // Check if user can manage an employee with target role
  const canManageEmployee = (targetRole: UserRole): boolean => {
    if (!user) return false;

    if (user.role === 'super_admin') {
      return true;
    }
    if (user.role === 'admin') {
      // Admins cannot manage other admins or super admins
      return !['super_admin', 'admin'].includes(targetRole);
    }
    if (user.role === 'manager') {
      // Managers can only manage employees
      return targetRole === 'employee';
    }
    return false;
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    isLoading,
    hasRole,
    hasPermission,
    canManageEmployee,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
